import React, { useState, useEffect } from "react";
import audioPlayer from "../services/audioPlayer";
import "./Sidebar.css";

const Sidebar = ({ sessions, activeSession, onNewSession, onSelectSession, onDeleteSession, onClearAll, username, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [showSettings, setShowSettings] = useState(false);

  const [autoSpeak, setAutoSpeak] = useState(() => localStorage.getItem("zanox_autoSpeak") !== "false");
  const [voiceSpeed, setVoiceSpeed] = useState(() => parseFloat(localStorage.getItem("zanox_voiceSpeed") || "0.9"));
  const [voicePitch, setVoicePitch] = useState(() => parseFloat(localStorage.getItem("zanox_voicePitch") || "1.1"));

  useEffect(() => {
    localStorage.setItem("zanox_autoSpeak", autoSpeak);
    localStorage.setItem("zanox_voiceSpeed", voiceSpeed);
    localStorage.setItem("zanox_voicePitch", voicePitch);
    audioPlayer.setConfig({ enabled: autoSpeak, rate: voiceSpeed, pitch: voicePitch });
  }, [autoSpeak, voiceSpeed, voicePitch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return Math.floor(diff / 60000) + "m ago";
    if (diff < 86400000) return Math.floor(diff / 3600000) + "h ago";
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const filteredSessions = sessions.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (s.title || "").toLowerCase().includes(q) || (s.sessionId || "").toLowerCase().includes(q);
  });

  return (
    <div>
      <button className={`sidebar-toggle ${collapsed ? "collapsed" : ""}`} onClick={() => setCollapsed(!collapsed)} title={collapsed ? "Expand" : "Collapse"}>
        <i className={`fas fa-chevron-${collapsed ? "right" : "left"}`}></i>
      </button>

      <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
        {!collapsed && (
          <div className="sidebar-inner">
            <div className="sidebar-header">
              <i className="fas fa-infinity"></i>
              <span>ZANOX AI</span>
            </div>

            {username && (
              <div className="sidebar-user">
                <i className="fas fa-user-circle"></i>
                <span>{username}</span>
              </div>
            )}

            <button className="new-session-btn" onClick={onNewSession}>
              <i className="fas fa-plus"></i>
              <span>New Inquiry</span>
            </button>

            <div className="sidebar-search">
              <i className="fas fa-search"></i>
              <input type="text" placeholder="Search sessions..." value={search} onChange={(e) => setSearch(e.target.value)} className="search-input" />
              {search && <button className="search-clear" onClick={() => setSearch("")}><i className="fas fa-times"></i></button>}
            </div>

            <div className="sidebar-divider"></div>

            <div className={`settings-panel ${showSettings ? "open" : ""}`}>
              <button className="settings-toggle" onClick={() => setShowSettings(!showSettings)}>
                <i className="fas fa-cog"></i><span>Settings</span>
                <i className={`fas fa-chevron-${showSettings ? "up" : "down"}`}></i>
              </button>
              {showSettings && (
                <div className="settings-content">
                  <div className="setting-item">
                    <div className="setting-label"><i className="fas fa-volume-up"></i><span>Auto-speak</span></div>
                    <label className="toggle-switch">
                      <input type="checkbox" checked={autoSpeak} onChange={(e) => setAutoSpeak(e.target.checked)} />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div className="setting-item">
                    <div className="setting-label"><i className="fas fa-tachometer-alt"></i><span>Speed: {voiceSpeed}x</span></div>
                    <input type="range" min="0.5" max="1.5" step="0.1" value={voiceSpeed} onChange={(e) => setVoiceSpeed(parseFloat(e.target.value))} className="setting-slider" />
                  </div>
                  <div className="setting-item">
                    <div className="setting-label"><i className="fas fa-wave-square"></i><span>Pitch: {voicePitch}</span></div>
                    <input type="range" min="0.5" max="2.0" step="0.1" value={voicePitch} onChange={(e) => setVoicePitch(parseFloat(e.target.value))} className="setting-slider" />
                  </div>
                  <button className="test-voice-btn" onClick={() => audioPlayer.playText("This is Zanox. Settings applied.")}>
                    <i className="fas fa-play"></i><span>Test Voice</span>
                  </button>
                  <button className="clear-all-btn" onClick={onClearAll}>
                    <i className="fas fa-trash-alt"></i><span>Clear All Sessions</span>
                  </button>
                </div>
              )}
            </div>

            <div className="sidebar-divider"></div>

            <div className="sessions-list">
              <span className="sessions-label">Sessions {search && `(${filteredSessions.length})`}</span>

              {filteredSessions.length === 0 && (
                <div className="no-sessions">
                  <i className="fas fa-search"></i>
                  <span>{search ? "No matches" : "No sessions yet"}</span>
                </div>
              )}

              {filteredSessions.map((session) => (
                <div key={`session-${session.sessionId}`} className={`session-item ${activeSession === session.sessionId ? "active" : ""}`} onClick={() => onSelectSession(session.sessionId)}>
                  <div className="session-info">
                    <i className="fas fa-comment"></i>
                    <div className="session-details">
                      <span className="session-title">{session.title || "New Inquiry"}</span>
                      <span className="session-meta">{session.messageCount || 0} msgs &middot; {formatDate(session.updatedAt || session.createdAt)}</span>
                    </div>
                  </div>
                  <button className="session-delete" onClick={(e) => { e.stopPropagation(); onDeleteSession(session.sessionId); }} title="Delete">
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>

            <div className="sidebar-footer">
              <div className="footer-item"><i className="fas fa-microphone"></i></div>
              <div className="footer-item"><i className="fas fa-image"></i></div>
              <div className="footer-item"><i className="fas fa-pen-fancy"></i></div>
              {onLogout && (
                <div className="footer-item logout-item" onClick={onLogout} title="Sign out">
                  <i className="fas fa-sign-out-alt"></i>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
};

export default Sidebar;
